import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Grid,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { pharmacyService } from '../services/pharmacyService';
import { bankAccountService } from '../services/bankAccountService';

const PharmacySetupPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [banks, setBanks] = useState([]);
  const [resolvingAccount, setResolvingAccount] = useState(false);
  const { setRequiresProfileSetup } = useAuth();
  const navigate = useNavigate();

  const steps = ['Basic Information', 'Contact Details', 'Address & Hours', 'Bank Account'];

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm();

  const accountNumber = watch('accountNumber');
  const bankCode = watch('bankCode');

  useEffect(() => {
    const loadBanks = async () => {
      try {
        const banksData = await bankAccountService.getBanks();
        setBanks(banksData || []);
      } catch (err) {
        console.error('Failed to load banks:', err);
      }
    };
    loadBanks();
  }, []);

  useEffect(() => {
    const resolveAccount = async () => {
      if (accountNumber && bankCode && accountNumber.length === 10) {
        setResolvingAccount(true);
        try {
          const resolved = await bankAccountService.resolveAccount(accountNumber, bankCode);
          setValue('accountName', resolved.accountName);
        } catch (err) {
          console.error('Failed to resolve account:', err);
        } finally {
          setResolvingAccount(false);
        }
      }
    };
    resolveAccount();
  }, [accountNumber, bankCode, setValue]);

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');

    try {
      // Format the data for the API
      const profileData = {
        pharmacyName: data.pharmacyName,
        licenseNumber: data.licenseNumber,
        contactInfo: {
          phone: data.phone,
          email: data.email
        },
        address: {
          street: data.street,
          city: data.city,
          state: data.state,
          country: data.country || 'Nigeria',
          coordinates: {
            latitude: parseFloat(data.latitude) || 0,
            longitude: parseFloat(data.longitude) || 0
          }
        },
        operatingHours: {
          monday: { open: data.mondayOpen, close: data.mondayClose },
          tuesday: { open: data.tuesdayOpen, close: data.tuesdayClose },
          wednesday: { open: data.wednesdayOpen, close: data.wednesdayClose },
          thursday: { open: data.thursdayOpen, close: data.thursdayClose },
          friday: { open: data.fridayOpen, close: data.fridayClose },
          saturday: { open: data.saturdayOpen, close: data.saturdayClose },
          sunday: { open: data.sundayOpen, close: data.sundayClose }
        }
      };

      await pharmacyService.createProfile(profileData);

      // If bank account details are provided, update bank account
      if (data.accountNumber && data.bankCode) {
        try {
          await bankAccountService.updatePharmacyBankAccount({
            accountNumber: data.accountNumber,
            bankCode: data.bankCode,
            accountName: data.accountName,
            bankName: data.bankName || banks.find(b => b.code === data.bankCode)?.name || ''
          });
        } catch (bankErr) {
          console.error('Failed to update bank account:', bankErr);
          setError('Profile created but bank account setup failed. You can add it later in settings.');
          setLoading(false);
          return;
        }
      }

      setRequiresProfileSetup(false);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Profile setup failed. Please try again.');
    }
    
    setLoading(false);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <TextField
              fullWidth
              label="Pharmacy Name"
              margin="normal"
              {...register('pharmacyName', { required: 'Pharmacy name is required' })}
              error={!!errors.pharmacyName}
              helperText={errors.pharmacyName?.message}
            />
            <TextField
              fullWidth
              label="License Number"
              margin="normal"
              {...register('licenseNumber', { required: 'License number is required' })}
              error={!!errors.licenseNumber}
              helperText={errors.licenseNumber?.message}
            />
          </Box>
        );

      case 1:
        return (
          <Box>
            <TextField
              fullWidth
              label="Phone Number"
              margin="normal"
              {...register('phone', { required: 'Phone number is required' })}
              error={!!errors.phone}
              helperText={errors.phone?.message}
            />
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              margin="normal"
              {...register('email', {
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
              error={!!errors.email}
              helperText={errors.email?.message}
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            <TextField
              fullWidth
              label="Street Address"
              margin="normal"
              {...register('street', { required: 'Street address is required' })}
              error={!!errors.street}
              helperText={errors.street?.message}
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="City"
                  margin="normal"
                  {...register('city', { required: 'City is required' })}
                  error={!!errors.city}
                  helperText={errors.city?.message}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="State"
                  margin="normal"
                  {...register('state', { required: 'State is required' })}
                  error={!!errors.state}
                  helperText={errors.state?.message}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Latitude (Optional)"
                  type="number"
                  margin="normal"
                  {...register('latitude')}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Longitude (Optional)"
                  type="number"
                  margin="normal"
                  {...register('longitude')}
                />
              </Grid>
            </Grid>
            
            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
              Operating Hours
            </Typography>
            
            {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
              <Grid container spacing={2} key={day} sx={{ mb: 1 }}>
                <Grid size={{ xs: 3 }}>
                  <Typography variant="body2" sx={{ mt: 2, textTransform: 'capitalize' }}>
                    {day}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <TextField
                    fullWidth
                    label="Open"
                    type="time"
                    margin="normal"
                    size="small"
                    {...register(`${day}Open`)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <TextField
                    fullWidth
                    label="Close"
                    type="time"
                    margin="normal"
                    size="small"
                    {...register(`${day}Close`)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            ))}
          </Box>
        );

      case 3:
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              Add your bank account details to receive payments. Your account will be verified automatically.
            </Alert>
            <FormControl fullWidth margin="normal">
              <InputLabel>Select Bank</InputLabel>
              <Select
                {...register('bankCode', { required: 'Please select a bank' })}
                label="Select Bank"
                error={!!errors.bankCode}
              >
                {banks.map((bank) => (
                  <MenuItem key={bank.code} value={bank.code}>
                    {bank.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.bankCode && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {errors.bankCode.message}
                </Typography>
              )}
            </FormControl>
            <TextField
              fullWidth
              label="Account Number"
              margin="normal"
              {...register('accountNumber', {
                required: 'Account number is required',
                pattern: {
                  value: /^\d{10}$/,
                  message: 'Account number must be 10 digits'
                }
              })}
              error={!!errors.accountNumber}
              helperText={errors.accountNumber?.message || 'Enter your 10-digit account number'}
            />
            <TextField
              fullWidth
              label="Account Name"
              margin="normal"
              {...register('accountName')}
              error={!!errors.accountName}
              helperText={errors.accountName?.message || (resolvingAccount ? 'Resolving account name...' : 'Account name will be resolved automatically')}
              disabled={resolvingAccount}
            />
            <TextField
              fullWidth
              label="Bank Name (Optional)"
              margin="normal"
              {...register('bankName')}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Card>
        <CardContent>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Complete Your Pharmacy Profile
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
            Please provide the following information to complete your pharmacy setup
          </Typography>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            {renderStepContent(activeStep)}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
              >
                Back
              </Button>

              {activeStep === steps.length - 1 ? (
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {loading ? 'Creating Profile...' : 'Complete Setup'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                >
                  Next
                </Button>
              )}
            </Box>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default PharmacySetupPage;
